/**
 * EditarMovimientoDialog — Edición manual de vouchers y pagos_reservacion desde
 * el timeline de la reserva. Reemplaza el flujo de "correcciones vía Claude
 * Code" para los casos frecuentes:
 *   - Corregir cuenta destino, tipo/método y referencia de un voucher o un pago
 *   - Reemplazar la imagen adjunta de un voucher
 *   - Anular un pago mal registrado (revertido=true, deja el registro visible)
 *
 * El MONTO no se edita. Un movimiento por la cifra equivocada no se corrige
 * reescribiéndola —eso borra lo que de verdad pasó—: se anula y se registra el
 * correcto. Las notas tampoco: el porqué del cambio va en el motivo, que es lo
 * que queda en la auditoría.
 *
 * Motivo obligatorio en TODOS los casos → queda en auditoria_operaciones.
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Box, Typography, Divider,
  CircularProgress, Alert, IconButton, Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import BlockIcon from '@mui/icons-material/Block';
import { API_BASE, getAuthHeaders, formatPrecio } from './shared/format';

// Métodos de pago que aplican a vouchers (mismo CHECK del backend)
const TIPO_SOPORTE_OPCIONES = [
  { v: 'efectivo',         label: 'Efectivo' },
  { v: 'transferencia',    label: 'Transferencia' },
  { v: 'tarjeta',          label: 'Tarjeta (datáfono)' },
  { v: 'datafono_agencia', label: 'Datáfono agencia' },
  { v: 'bold_link',        label: 'Link Bold' },
  { v: 'epayco_link',      label: 'Link ePayco' },
];

// Métodos de pago para pagos_reservacion (más flexible — string libre en BD)
const METODO_PAGO_OPCIONES = [
  { v: 'efectivo',         label: 'Efectivo' },
  { v: 'transferencia',    label: 'Transferencia' },
  { v: 'tarjeta',          label: 'Tarjeta (datáfono)' },
  { v: 'tarjeta_credito',  label: 'Tarjeta de crédito' },
  { v: 'tarjeta_debito',   label: 'Tarjeta débito' },
  { v: 'datafono',         label: 'Datáfono' },
  { v: 'nequi',            label: 'Nequi' },
  { v: 'wompi',            label: 'Wompi' },
  { v: 'bold',             label: 'Bold' },
  { v: 'bold_link',        label: 'Link Bold' },
  { v: 'epayco_link',      label: 'Link ePayco' },
];

export default function EditarMovimientoDialog({
  open, onClose, movimiento, cuentasDestino = [], usuario, onSaved,
}) {
  const esVoucher = movimiento?.tipo === 'voucher';
  const esPago = movimiento?.tipo === 'pago_contable';
  const id = esVoucher ? movimiento?.metadata?.voucher_id : movimiento?.metadata?.pago_id;

  const [referencia, setReferencia] = useState('');
  const [tipoMetodo, setTipoMetodo] = useState('');
  const [cuentaDestinoId, setCuentaDestinoId] = useState('');
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [anulando, setAnulando] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Cambiar la cuenta o el método de un cobro de muelle puede descuadrar un
  // cierre ya firmado. El servidor lo frena una vez y acá se ofrece confirmar.
  const [avisoCierre, setAvisoCierre] = useState(null);

  useEffect(() => {
    if (!movimiento) return;
    setReferencia(movimiento.referencia || '');
    setTipoMetodo(esVoucher
      ? (movimiento.subtipo || '')                       // el voucher sí guarda su tipo ahí
      : (movimiento.metadata?.metodo_pago
         || movimiento.metadata?.metodo_pago_usado || ''));
    setCuentaDestinoId(movimiento.metadata?.cuenta_destino_id ??
                       movimiento.metadata?.cuenta_id ?? '');
    setMotivo('');
    setError(null);
    setAnulando(false);
    setAvisoCierre(null);
  }, [movimiento?.metadata?.voucher_id, movimiento?.metadata?.pago_id]); // eslint-disable-line

  const opcionesMetodo = useMemo(
    () => (esVoucher ? TIPO_SOPORTE_OPCIONES : METODO_PAGO_OPCIONES),
    [esVoucher]
  );

  if (!movimiento) return null;

  const handleGuardar = async (confirmandoCierre = false) => {
    if (!motivo.trim()) {
      setError('El motivo es obligatorio.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = {
        motivo: motivo.trim(),
        usuario_id: usuario?.id || null,
        usuario_nombre: usuario?.nombre || null,
      };
      // Solo enviar campos que cambien respecto al original
      // El monto y las notas no son editables desde este diálogo: aunque el
      // endpoint los acepte, acá nunca se envían.
      if (referencia !== (movimiento.referencia || '')) {
        body.referencia = referencia || null;
      }
      if (esVoucher && tipoMetodo && tipoMetodo !== movimiento.subtipo) {
        body.tipo_soporte = tipoMetodo;
      }
      if (esPago && tipoMetodo !== (movimiento.metadata?.metodo_pago_usado || '')) {
        body.metodo_pago = tipoMetodo || null;
      }
      // cuenta_destino_id: 0 = quitar, número = asignar
      const cuentaActual = movimiento.metadata?.cuenta_destino_id ?? movimiento.metadata?.cuenta_id ?? null;
      const cuentaNueva = cuentaDestinoId === '' ? null : Number(cuentaDestinoId);
      if (cuentaNueva !== cuentaActual) {
        body.cuenta_destino_id = cuentaNueva === null ? (esPago ? 0 : null) : cuentaNueva;
      }
      // La cuenta de destino de un pago vive en TRES sitios: el pago, la
      // reserva y el voucher que lo respalda. El endpoint viejo solo toca el
      // primero, y los informes de ventas leen el del voucher: por eso ese
      // cambio va por /correcciones, que los sincroniza y avisa si descuadra
      // un cierre firmado. El resto —monto, notas, anular— sigue igual.
      if (esPago && (body.cuenta_destino_id !== undefined
                     || body.metodo_pago !== undefined
                     || body.referencia !== undefined)) {
        const rc = await fetch(
          `${API_BASE}/turismo/correcciones/pagos/${id}/cuenta-destino`, {
            method: 'POST', headers: getAuthHeaders(),
            body: JSON.stringify({
              cuenta_destino_id: Number(cuentaDestinoId) || null,
              metodo_pago: body.metodo_pago ?? null,
              referencia: body.referencia ?? null,
              confirmar_cierre_firmado: confirmandoCierre,
            }),
          });
        const dc = await rc.json().catch(() => ({}));
        if (!rc.ok) {
          const det = dc.detail;
          if (det?.code === 'CIERRE_FIRMADO') {
            setAvisoCierre(det.message); setSaving(false); return;
          }
          throw new Error(det?.message || det || `Error ${rc.status}`);
        }
        // Ya quedaron aplicados por el servicio: no se repiten abajo.
        delete body.cuenta_destino_id;
        delete body.metodo_pago;
        delete body.referencia;
        if (Object.keys(body).length <= 3) {   // solo motivo y usuario
          onSaved?.(); onClose(); setSaving(false); return;
        }
      }

      const path = esVoucher
        ? `/turismo/vouchers/${id}/editar`
        : `/turismo/pagos-reservacion/${id}/editar`;

      const r = await fetch(`${API_BASE}${path}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${r.status}`);
      }
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReemplazarImagen = async (file) => {
    if (!file) return;
    if (!motivo.trim()) {
      setError('Antes de reemplazar la imagen escribe el motivo abajo.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      // 1) Sube el archivo
      const fd = new FormData();
      fd.append('archivo', file);
      const rUp = await fetch(`${API_BASE}/turismo/soportes/upload`, {
        method: 'POST', body: fd,
      });
      if (!rUp.ok) throw new Error('Error al subir el archivo');
      const { url } = await rUp.json();

      // 2) Llama al endpoint de reemplazar imagen
      const rEd = await fetch(`${API_BASE}/turismo/vouchers/${id}/reemplazar-imagen`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imagen_url: url,
          motivo: motivo.trim(),
          usuario_id: usuario?.id || null,
          usuario_nombre: usuario?.nombre || null,
        }),
      });
      if (!rEd.ok) {
        const err = await rEd.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${rEd.status}`);
      }
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  };

  const handleAnular = async () => {
    if (!motivo.trim()) {
      setError('El motivo es obligatorio para anular.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/turismo/pagos-reservacion/${id}/anular`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          motivo: motivo.trim(),
          usuario_id: usuario?.id || null,
          usuario_nombre: usuario?.nombre || null,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${r.status}`);
      }
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      // En un portátil el contenido no cabía y los campos de abajo —el motivo
      // y la zona de peligro— quedaban fuera de vista sin que se notara. Con
      // el alto acotado y el cuerpo desplazable, siempre se ve dónde termina.
      PaperProps={{ sx: { maxHeight: { xs: '100%', sm: '92vh' } } }}
      fullScreen={typeof window !== 'undefined' && window.innerWidth < 600}>
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
        color: '#fff', py: 1.5,
      }}>
        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
            {esVoucher ? 'Editar voucher' : 'Editar pago'}
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#cbd5e1' }}>
            #{id} · {formatPrecio(movimiento.monto)}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2.5, overflowY: 'auto' }}>
        {error && <Alert severity="error" sx={{ mb: 1.5, fontSize: 12 }}>{error}</Alert>}
        {/* El cierre del día ya cuadró y este cambio lo mueve de casilla. No
            es un error: es algo que alguien tiene que decidir a sabiendas. */}
        {avisoCierre && (
          <Alert severity="warning" sx={{ mb: 1.5, fontSize: 12 }}
                 action={
                   <Button size="small" disabled={saving}
                           onClick={() => { setAvisoCierre(null); handleGuardar(true); }}>
                     Hacerlo igual
                   </Button>
                 }>
            {avisoCierre}
          </Alert>
        )}

        <Box sx={{ display: 'grid', gap: 1.5, mb: 1.5,
                   gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          {/* El monto se muestra, no se edita: un movimiento por la cifra
              equivocada no se arregla reescribiéndola —eso borra lo que de
              verdad pasó—, se anula y se registra el correcto. */}
          <Box sx={{ px: 1.5, py: 1, borderRadius: 1, bgcolor: '#f8fafc',
                     border: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontSize: 10.5, color: '#94a3b8', fontWeight: 700,
                              textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Monto
            </Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>
              {formatPrecio(movimiento.monto)}
            </Typography>
          </Box>
          <TextField label={esVoucher ? 'Referencia' : 'Referencia (opcional)'}
            size="small" value={referencia} onChange={e => setReferencia(e.target.value)} />
        </Box>

        <Box sx={{ display: 'grid', gap: 1.5, mb: 1.5,
                   gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          <TextField label={esVoucher ? 'Tipo (método)' : 'Método de pago'}
            select size="small" value={tipoMetodo} onChange={e => setTipoMetodo(e.target.value)}>
            <MenuItem value="">— Sin especificar —</MenuItem>
            {opcionesMetodo.map(o => (
              <MenuItem key={o.v} value={o.v}>{o.label}</MenuItem>
            ))}
          </TextField>

          <TextField label="Cuenta destino" select size="small"
            value={cuentaDestinoId} onChange={e => setCuentaDestinoId(e.target.value)}>
            <MenuItem value="">— Sin cuenta —</MenuItem>
            {cuentasDestino.map(c => (
              <MenuItem key={c.id} value={c.id}>{c.nombre}</MenuItem>
            ))}
          </TextField>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <TextField label="Motivo del cambio (obligatorio · queda en auditoría)"
          fullWidth multiline minRows={2} size="small" required
          value={motivo} onChange={e => setMotivo(e.target.value)}
          placeholder="Ej. El cobro entró a la cuenta de Jesus Antonio Corrales, no a Bancolombia TMA"
          sx={{ mb: 1 }} />

        {/* Acción secundaria: reemplazar imagen (solo vouchers) */}
        {esVoucher && (
          <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#f8fafc', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontSize: 11, color: '#475569', mb: 0.75 }}>
              {movimiento.imagen_url
                ? 'Este voucher tiene imagen adjunta. Puedes reemplazarla:'
                : 'Este voucher no tiene imagen. Puedes adjuntar una:'}
            </Typography>
            <Button component="label" size="small" variant="outlined" startIcon={<UploadFileIcon />}
              disabled={uploading || !motivo.trim()}
              sx={{ textTransform: 'none', fontSize: 12 }}>
              {uploading ? 'Subiendo…' : 'Reemplazar imagen'}
              <input hidden type="file" accept="image/*,application/pdf"
                onChange={e => handleReemplazarImagen(e.target.files?.[0])} />
            </Button>
            {!motivo.trim() && (
              <Typography sx={{ fontSize: 10, color: '#94a3b8', mt: 0.5, fontStyle: 'italic' }}>
                Escribe el motivo arriba primero.
              </Typography>
            )}
          </Box>
        )}

        {/* Acción destructiva: anular pago (solo pagos_reservacion) */}
        {esPago && (
          <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#fef2f2', borderRadius: 1.5, border: '1px solid #fecaca' }}>
            {!anulando ? (
              <>
                <Typography sx={{ fontSize: 11, color: '#7f1d1d', mb: 0.75 }}>
                  Zona de peligro: si el pago está totalmente equivocado, puedes anularlo.
                  No se borra — queda marcado como revertido con el motivo.
                </Typography>
                <Button size="small" variant="outlined" startIcon={<BlockIcon />}
                  onClick={() => setAnulando(true)}
                  sx={{ textTransform: 'none', fontSize: 12,
                        borderColor: '#dc2626', color: '#dc2626',
                        '&:hover': { borderColor: '#b91c1c', bgcolor: '#fef2f2' } }}>
                  Anular este pago
                </Button>
              </>
            ) : (
              <>
                <Typography sx={{ fontSize: 11, color: '#7f1d1d', mb: 1, fontWeight: 700 }}>
                  ¿Confirmas anular el pago #{id} por {formatPrecio(movimiento.monto)}?
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" variant="outlined"
                    onClick={() => setAnulando(false)}
                    sx={{ textTransform: 'none', fontSize: 12 }}>Cancelar</Button>
                  <Button size="small" variant="contained" color="error"
                    disabled={saving || !motivo.trim()}
                    onClick={handleAnular}
                    sx={{ textTransform: 'none', fontSize: 12 }}>
                    {saving ? 'Anulando…' : 'Sí, anular'}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancelar</Button>
        {!anulando && (
          <Button variant="contained" onClick={() => handleGuardar(false)}
            disabled={saving || !motivo.trim()}
            sx={{ textTransform: 'none',
                  background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)' }}>
            {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Guardar cambios'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
